<script setup lang="ts">
import { ref } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

interface Props {
  label: string
  title: string
  description: string
  formula: string
}

defineProps<Props>()

// Small grace window so the user can move the cursor from the trigger into
// the panel without the panel disappearing mid-traverse.
const HOVER_CLOSE_DELAY_MS = 100

const visible = ref(false)
let hideTimer: ReturnType<typeof setTimeout> | null = null

const show = () => {
  if (hideTimer) clearTimeout(hideTimer)
  visible.value = true
}

const hide = () => {
  hideTimer = setTimeout(() => {
    visible.value = false
  }, HOVER_CLOSE_DELAY_MS)
}
</script>

<template>
  <Box class="kit" @mouseenter="show" @mouseleave="hide">
    <slot />

    <Transition name="kit-fade">
      <Box
        v-if="visible"
        direction="col"
        gap="2"
        class="kit__panel"
        @mouseenter="show"
        @mouseleave="hide"
        @click.stop
      >
        <Box direction="col" gap="1">
          <Text type="micro" is="span" class="kit__label">{{ label }}</Text>
          <Text type="body-sm" weight="semibold" is="span" class="kit__title">{{ title }}</Text>
        </Box>
        <Text type="caption-sm" color="muted" is="p" class="kit__desc">{{ description }}</Text>
        <Box align="center" class="kit__formula">
          <Text type="caption-sm" is="span" class="kit__formula-text">{{ formula }}</Text>
        </Box>
      </Box>
    </Transition>
  </Box>
</template>

<style scoped>
.kit {
  position: relative;
  display: inline-flex;
}

.kit__panel {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 240px;
  background: var(--p-surface-0);
  border: 1px solid var(--p-primary-200);
  border-radius: 12px;
  padding: 14px 1rem;
  z-index: 200;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
}

.kit__label {
  letter-spacing: 0.04em;
  line-height: 1.25;
}

.kit__title {
  line-height: 1.3;
}

.kit__desc {
  line-height: 1.5;
  margin: 0;
}

.kit__formula {
  background: var(--p-content-border-color);
  border-radius: 8px;
  padding: 7px 11px;
}

.kit__formula-text {
  color: var(--p-text-muted-color);
  line-height: 1.4;
}

.kit-fade-enter-active,
.kit-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.kit-fade-enter-from,
.kit-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
